import { useEffect, useRef, useState } from "react";
import { DownloadButton, PrimaryButton, TextButton } from "../../../Component/Buttons";
import { DataTableComponent } from "../../../Component/DataTable";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { CustomSearchInput, CustomSelectBox } from "../../../Component/FormElements";
import { Loader } from "../../../Component/Others";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const ITEMS_PER_PAGE = 20;

const PAGE_SORT_FIELDS = [
    'product_name',
    'product_code',
    'category',
    'type',
    'finish',
    'innovation',
    'ltrs_sold',
];

const ProductDetailPage = () => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const navigate = useNavigate();
    const { state } = useLocation();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [toggleFilter, setToggleFilter] = useState(false);

    const [filterData, setFilterData] = useState({});


    const [selectedFinish, setSelectedFinish] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedInnovation, setSelectedInnovation] = useState(null);


    const [searchValue, setSearchValue] = useState("");
    const [clearFilter, setClearFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [loader, setLoader] = useState(true);
    const [productList, setProductList] = useState({});

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        _getFilterData();

        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            _getProductList();
        } else {
            navigate("/");
        }
    }, [state, searchValue, currentPage, clearFilter]);


    const _handleClearAll = () => {

        setSelectedCategory(filterData?.category?.[0] || null);
        setSelectedType(filterData?.type?.[0] || null);
        setSelectedInnovation(filterData?.innovation?.[0] || null);
        setSelectedFinish(filterData?.finish?.[0] || null);

        setSortField(null);
        setSortDirection(null);
        setClearFilter(true);
        setCurrentPage(1);
    };

    // handle page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoader(true);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoader(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getProductList(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // API - Get Filter data
    const _getProductList = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling-product/list';
        let data = {
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "query": searchValue,
            "type": selectedType?.value || null,
            "innovation": selectedInnovation?.value || null,
            "category": selectedCategory?.value || null,
            "finish": selectedFinish?.value || null,
            "scope": state?.scope,
            "start_date": state?.start_date || null,
            "end_date": state?.end_date || null,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response?.status_code === 200) {
                const list = Array.isArray(response.data?.data) ? response.data.data : [];
                setProductList({
                    ...(response.data || {}),
                    data: list,
                    total_count: response.data?.total_count || 0,
                });
            } else {
                setProductList({ data: [], total_count: 0 });
            }
            setLoader(false);
            setClearFilter(false);
        }).catch(() => {
            setProductList({ data: [], total_count: 0 });
            setLoader(false);
            setClearFilter(false);
        });
    };

    // API - Get Filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = 'CA/dashboard/filter-data';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response?.status_code === 200) {
                setFilterData(response.data || {});

                setSelectedCategory(response.data?.category?.[0] || null);
                setSelectedType(response.data?.type?.[0] || null);
                setSelectedInnovation(response.data?.innovation?.[0] || null);
                setSelectedFinish(response.data?.finish?.[0] || null);
            }
        }).catch(() => { });
    };


    // API - Download list
    const _download = () => {
        setDownloadLoader(true);
        let is_post = true;
        let url = 'CA/dashboard/get-top-selling-product/download';
        let data = {
            "type": selectedType?.value || null,
            "innovation": selectedInnovation?.value || null,
            "category": selectedCategory?.value || null,
            "scope": state?.scope,
            "start_date": state?.start_date || null,
            "end_date": state?.end_date || null,
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const link = document.createElement('a');
                link.href = response.file_url;
                link.download = "product_list";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };


    const TABLE_COLUMN = [
        {
            name: 'Product code',
            id: 'product_code',
            sortField: 'product_code',
            selector: row => row.product_code || "-",
            width: '140px',
            sortable: true,
        },
        {
            name: 'Product name',
            id: 'product_name',
            sortField: 'product_name',
            selector: row => row.product_name,
            cell: row =>
                <div className='d-flex gap-1 align-items-center'>
                    <p className='mb-0'>
                        {row.product_name}
                    </p>
                </div>,
            width: '280px',
            sortable: true,
        },
        {
            name: 'Category',
            id: 'category',
            sortField: 'category',
            selector: row => row.category || "-",
            width: '110px',
            sortable: true,
        },
        {
            name: 'Type',
            id: 'type',
            sortField: 'type',
            selector: row => row.type || "-",
            width: '110px',
            sortable: true,
        },
        {
            name: 'Finish',
            id: 'finish',
            sortField: 'finish',
            selector: row => row.finish || "-",
            width: '110px',
            sortable: true,
        },
        {
            name: 'Innovation',
            id: 'innovation',
            sortField: 'innovation',
            selector: row => row.innovation || "-",
            width: '110px',
            sortable: true,
        },

        {
            name: PROFILE_DATA?.country === "Egypt" ? 'Units sold' : 'Ltrs sold',
            id: 'ltrs_sold',
            sortField: 'ltrs_sold',
            selector: row => row?.ltrs_sold || "-",
            width: '110px',
            sortable: true,
        },
        {
            name: 'Action',
            cell: row => row.product_code ?
                <TextButton name="View breakdown"
                    className="w-max-content e-font-weight-400"
                    icon={true}
                    onPress={() => {
                        navigate("/product-detail/sales-breakdown", {
                            state: {
                                product_code: row.product_code,
                                product_name: row.product_name,
                                scope: state?.scope,
                                start_date: state?.start_date || null,
                                end_date: state?.end_date || null,
                            }
                        });
                    }}
                />
                : "-",
            width: "150px",
            ignoreRowClick: true,
        },
    ];

    const tableRows = Array.isArray(productList?.data) ? productList.data : [];

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='e-display-flex cursor-pointer w-max-content'
                    onClick={() => navigate("/")} >
                    <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                    />
                    <h1 className='ps-1 mb-0  e-primary-font-playfair e-font-24 line-height-32px e-text-raisin-black e-font-weight-600'>
                        Product details
                    </h1>
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-border-1px-platinum-dark e-admin-box-shadow py-4 e-border-radius-16'>
                    <div className='px-3'>
                        <div className='pb-2 d-flex align-items-center justify-content-end'>

                            <div className={`d-flex align-items-center gap-3`}>

                                <DownloadButton loader={downloadLoader}
                                    disabled={loader || tableRows.length === 0}
                                    onClick={_download} />

                                <CustomSearchInput placeholder={"Search by name,ID or store"}
                                    type="text"
                                    require
                                    value={searchValue}
                                    className="w-320px padding-8px-tb py-2 "
                                    labelStyle="e-font-weight-500"
                                    iconClass="top-10px"
                                    handleChange={(e) => {
                                        setSearchValue(e);
                                        setCurrentPage(1);
                                    }}
                                    handleClear={() => {
                                        setSearchValue("");
                                        setSortField(null);
                                        setSortDirection(null);
                                        setCurrentPage(1);
                                    }} />


                                <div>
                                    <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                        draggable={false}
                                        className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                        alt='filter'
                                        onClick={() => setToggleFilter(!toggleFilter)} />
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mb-3'>
                            <div className='row'>

                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Category"
                                        value={selectedCategory}
                                        options={filterData?.category || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        labelClass="e-font-weight-600"
                                        wrapperHeight="0px"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedCategory(value)}
                                    />
                                </div>
                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Type"
                                        value={selectedType}
                                        options={filterData?.type || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        labelClass="e-font-weight-600"
                                        wrapperHeight="0px"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedType(value)}
                                    />
                                </div>
                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Innovation/Old"
                                        value={selectedInnovation}
                                        options={filterData?.innovation || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        labelClass="e-font-weight-600"
                                        wrapperHeight="0px"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedInnovation(value)}
                                    />
                                </div>

                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Finish"
                                        value={selectedFinish}
                                        options={filterData?.finish || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        labelClass="e-font-weight-600"
                                        wrapperHeight="0px"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedFinish(value)}
                                    />
                                </div>

                                <div className='pt-3 d-flex align-items-center gap-3 justify-content-end'>
                                    <TextButton name="Clear all"
                                        onPress={_handleClearAll} />
                                    <PrimaryButton name="Filter now"
                                        className="px-3 py-1 e-border-radius-8"
                                        onPress={() => {
                                            setCurrentPage(1);
                                            setLoader(true);
                                            _getProductList(1);
                                        }} />
                                </div>
                            </div>
                        </div>
                    }

                    {loader ?
                        <Loader />
                        :
                        <>
                            {tableRows.length > 0 ?
                                <div className={`e-members-table`}>
                                    <DataTableComponent
                                        key={`product-detail-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMN}
                                        data={tableRows}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationTotalRows={productList?.total_count || 0}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                // empty screen
                                <div className='py-5 my-5'>
                                    <EmptyScreen message="No products found" />
                                </div>
                            }

                        </>
                    }

                </div>
            </div>

        </section>
    )
}

export default ProductDetailPage;
